import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { getUser, logout, saveAuth } from '../../services/auth';
import {
  MdCalendarToday,
  MdLocationOn,
  MdAttachMoney,
  MdMenu,
  MdClose,
  MdPeople,
  MdWork,
  MdCreditCard,
  MdSearch,
  MdChevronLeft,
  MdChevronRight,
  MdHome,
  MdLogin,
  MdDelete,
  MdSettings,
  MdPerson,
  MdLogout,
  MdNotifications,
  MdInfo,
  MdBuild,
  MdPlayArrow,
} from 'react-icons/md';
import api from '../../services/api';

const FRONTEND_VERSION = (typeof window !== 'undefined' && window.__APP_BUILD__?.version) || '1.0.5';
import { useToast } from '../../components/ToastContainer';
import ToastContainer from '../../components/ToastContainer';
import ConfirmDialog from '../../components/ConfirmDialog';
import logoImage from '../../assets/babysit_logo.png';
import { FaUsers, FaClipboardList, FaCreditCard } from 'react-icons/fa';
import { useIsMobile } from '../../hooks/useIsMobile';

const PAGE_SIZE_USERS = 15;
const PAGE_SIZE_JOBS = 20;
const ROLES = [
  { value: '', label: 'Sve uloge' },
  { value: 'parent', label: 'Roditelj' },
  { value: 'babysitter', label: 'Bebisiterka' },
  { value: 'admin', label: 'Admin' },
  { value: 'platform_admin', label: 'Platform admin' },
];
const JOB_STATUSES = [
  { value: '', label: 'Svi statusi' },
  { value: 'posted', label: 'Objavljen' },
  { value: 'accepted', label: 'Prihvaćen' },
  { value: 'in_progress', label: 'U toku' },
  { value: 'completed', label: 'Završen' },
  { value: 'cancelled', label: 'Otkazan' },
];

export default function AdminPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'users';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobsPagination, setJobsPagination] = useState({ current: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, id: null });
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [jobSearch, setJobSearch] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState('');
  const [jobPage, setJobPage] = useState(1);
  const [appInfo, setAppInfo] = useState(null);
  const [adminConfig, setAdminConfig] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [runRemindersLoading, setRunRemindersLoading] = useState(false);
  const { showToast, removeToast, toasts } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const user = getUser();
  const isAdmin = user && (user.role === 'admin' || user.role === 'platform_admin');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    setActiveTab(tabFromUrl);
  }, [tabFromUrl, isAdmin, navigate]);

  const setTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadData();
  }, [activeTab, isAdmin, userPage, jobPage, jobStatusFilter]);

  useEffect(() => {
    if (!isAdmin || activeTab !== 'settings') return;
    setSettingsLoading(true);
    Promise.all([
      api.get('/admin/app-info').catch(() => ({ data: null })),
      api.get('/admin/config').catch(() => ({ data: null })),
    ])
      .then(([infoRes, configRes]) => {
        setAppInfo(infoRes?.data || null);
        setAdminConfig(configRes?.data || null);
      })
      .finally(() => setSettingsLoading(false));
  }, [activeTab, isAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const response = await api.get('/users');
        setUsers(response.data || []);
      } else if (activeTab === 'jobs') {
        const params = new URLSearchParams({ page: jobPage, limit: PAGE_SIZE_JOBS });
        if (jobStatusFilter) params.set('status', jobStatusFilter);
        const response = await api.get(`/jobs?${params}`).catch(() => ({ data: { jobs: [], pagination: { current: 1, pages: 1, total: 0 } } }));
        const data = response.data;
        setJobs(data?.jobs || []);
        setJobsPagination(data?.pagination || { current: 1, pages: 1, total: 0 });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Greška pri učitavanju.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let list = users;
    const q = userSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (u) =>
          (u.firstName || '').toLowerCase().includes(q) ||
          (u.lastName || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.phone || '').toLowerCase().includes(q)
      );
    }
    if (userRoleFilter) list = list.filter((u) => u.role === userRoleFilter);
    return list;
  }, [users, userSearch, userRoleFilter]);

  const filteredJobs = useMemo(() => {
    if (!jobSearch.trim()) return jobs;
    const q = jobSearch.trim().toLowerCase();
    return jobs.filter(
      (j) =>
        (j.title || '').toLowerCase().includes(q) ||
        (j.description || '').toLowerCase().includes(q)
    );
  }, [jobs, jobSearch]);

  const totalUserPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE_USERS));
  const paginatedUsers = useMemo(() => {
    const start = (userPage - 1) * PAGE_SIZE_USERS;
    return filteredUsers.slice(start, start + PAGE_SIZE_USERS);
  }, [filteredUsers, userPage]);

  const handleLoginAs = async (userId) => {
    try {
      const { data } = await api.post('/auth/login-as', { userId });
      saveAuth(data.token, data.user);
      showToast('Ulogovani ste kao korisnik.', 'success');
      navigate('/dashboard', { replace: true });
      window.location.reload();
    } catch (err) {
      showToast(err.response?.data?.message || 'Greška pri Login as.', 'error');
    }
  };

  const handleDeleteUser = (userId) => {
    setDeleteConfirm({ isOpen: true, type: 'user', id: userId });
  };

  const confirmDeleteUser = async () => {
    const userId = deleteConfirm.id;
    if (!userId) return;
    try {
      await api.delete(`/users/${userId}`);
      loadData();
      setDeleteConfirm({ isOpen: false, type: null, id: null });
      showToast('Korisnik je uspešno obrisan.', 'success');
    } catch (error) {
      showToast('Greška pri brisanju: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleDeleteJob = (jobId) => {
    setDeleteConfirm({ isOpen: true, type: 'job', id: jobId });
  };

  const handleRunReminders = async () => {
    setRunRemindersLoading(true);
    try {
      await api.post('/admin/run-reminders');
      showToast('Posao podsetnika je pokrenut. Proverite email i obaveštenja.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Greška pri pokretanju podsetnika.', 'error');
    } finally {
      setRunRemindersLoading(false);
    }
  };

  const confirmDeleteJob = async () => {
    const jobId = deleteConfirm.id;
    if (!jobId) return;
    try {
      await api.delete(`/jobs/${jobId}`);
      loadData();
      setDeleteConfirm({ isOpen: false, type: null, id: null });
      showToast('Posao je uspešno obrisan.', 'success');
    } catch (error) {
      showToast('Greška pri brisanju: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  if (!isAdmin) return null;

  const tabs = [
    { id: 'users', label: 'Korisnici', icon: MdPeople },
    { id: 'jobs', label: 'Poslovi', icon: MdWork },
    { id: 'payments', label: 'Plaćanja', icon: MdCreditCard },
    { id: 'settings', label: 'Podešavanja', icon: MdSettings },
  ];
  const mobileNavItems = [
    { path: '/dashboard/admin', tab: null, icon: MdHome, label: 'Dashboard' },
    { path: '/admin', tab: 'users', icon: FaUsers, label: 'Korisnici' },
    { path: '/admin', tab: 'jobs', icon: FaClipboardList, label: 'Poslovi' },
    { path: '/admin', tab: 'payments', icon: FaCreditCard, label: 'Plaćanja' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-20 md:pb-0">
      {/* Top Bar: logo + menu (responsive) */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Link to="/admin" className="flex items-center gap-2 min-w-0 shrink-0">
              <img src={logoImage} alt="Nannity" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-primary-600 dark:text-primary-300 truncate">Nannity</span>
            </Link>
            <Link to="/dashboard/admin" className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
              <MdHome className="text-lg" /> Dashboard
            </Link>
          </div>

          {/* Desktop: user menu (like regular dashboard) */}
          <div className="hidden md:flex items-center gap-2 relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen((o) => !o)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left min-w-0"
            >
              <MdPerson className="text-xl text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-w-[140px]">
                {user?.firstName} {user?.lastName}
              </span>
            </button>
            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-30" aria-hidden="true" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-56 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-lg z-40 py-1">
                  <Link
                    to="/admin?tab=settings"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <MdSettings className="text-lg" /> Podešavanja
                  </Link>
                  <button
                    type="button"
                    onClick={() => { setUserMenuOpen(false); logout(); navigate('/login'); }}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
                  >
                    <MdLogout className="text-lg" /> Odjavi se
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile: hamburger */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Meni"
          >
            {mobileMenuOpen ? <MdClose className="text-xl" /> : <MdMenu className="text-xl" />}
          </button>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 flex flex-col gap-2">
            <Link to="/dashboard/admin" className="flex items-center gap-2 py-2 text-gray-700 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
              <MdHome /> Admin Dashboard
            </Link>
            <Link to="/admin?tab=settings" className="flex items-center gap-2 py-2 text-gray-700 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
              <MdSettings /> Podešavanja
            </Link>
            <p className="text-sm text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-700">
              {user?.firstName} {user?.lastName}
            </p>
            <button
              onClick={() => { setMobileMenuOpen(false); logout(); navigate('/login'); }}
              className="text-left py-2 text-red-600 dark:text-red-400 flex items-center gap-2"
            >
              <MdLogout /> Odjavi se
            </button>
          </div>
        )}
      </header>

      {/* Desktop tabs */}
      <div className="hidden md:block bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 py-3 px-4 border-b-2 transition-colors text-sm font-medium ${
                  activeTab === t.id
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
              >
                <t.icon className="text-lg" /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-6 flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent" />
          </div>
        ) : (
          <>
            {activeTab === 'users' && (
              <div className="card">
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Korisnici</h2>
                  <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-initial min-w-[160px]">
                      <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                      <input
                        type="text"
                        placeholder="Pretraga (ime, email, telefon)..."
                        value={userSearch}
                        onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
                        className="input-base pl-9 w-full"
                      />
                    </div>
                    <select
                      value={userRoleFilter}
                      onChange={(e) => { setUserRoleFilter(e.target.value); setUserPage(1); }}
                      className="input-base w-full sm:w-auto min-w-[140px]"
                    >
                      {ROLES.map((r) => (
                        <option key={r.value || 'all'} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {paginatedUsers.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">Nema korisnika.</p>
                ) : (
                  <>
                    {isMobile ? (
                      <div className="space-y-3">
                        {paginatedUsers.map((u) => (
                          <div key={u.id} className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                            <div className="flex flex-col gap-2">
                              <div className="font-medium text-gray-900 dark:text-gray-100">{u.firstName} {u.lastName}</div>
                              <a href={`mailto:${u.email}`} className="text-sm text-primary-600 dark:text-primary-400 truncate" title={u.email}>{u.email}</a>
                              <span className={`inline-block w-fit px-2 py-0.5 rounded text-xs font-medium ${
                                u.role === 'platform_admin' || u.role === 'admin' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                u.role === 'parent' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300' :
                                'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              }`}>
                                {u.role}
                              </span>
                              <div className="flex flex-wrap items-center gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={() => handleLoginAs(u.id)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors"
                                  title="Login as"
                                >
                                  <MdLogin className="text-lg" /> Login as
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                                  title="Obriši"
                                >
                                  <MdDelete className="text-lg" /> Obriši
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300">Ime</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300">Email</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300">Uloga</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-700 dark:text-gray-300">Akcije</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedUsers.map((u) => (
                              <tr key={u.id} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="py-3 px-3">{u.firstName} {u.lastName}</td>
                                <td className="py-3 px-3 truncate max-w-[180px]" title={u.email}>{u.email}</td>
                                <td className="py-3 px-3">
                                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                    u.role === 'platform_admin' || u.role === 'admin' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                    u.role === 'parent' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300' :
                                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                  }`}>
                                    {u.role}
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleLoginAs(u.id)}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors"
                                      title="Login as"
                                    >
                                      <MdLogin className="text-lg" /> Login as
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteUser(u.id)}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                                      title="Obriši"
                                    >
                                      <MdDelete className="text-lg" /> Obriši
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {totalUserPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Prikazano {((userPage - 1) * PAGE_SIZE_USERS) + 1}–{Math.min(userPage * PAGE_SIZE_USERS, filteredUsers.length)} od {filteredUsers.length}
                        </p>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setUserPage((p) => Math.max(1, p - 1))} disabled={userPage <= 1} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                            <MdChevronLeft className="text-lg" />
                          </button>
                          <span className="flex items-center px-3 text-sm">Strana {userPage} / {totalUserPages}</span>
                          <button type="button" onClick={() => setUserPage((p) => Math.min(totalUserPages, p + 1))} disabled={userPage >= totalUserPages} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                            <MdChevronRight className="text-lg" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'jobs' && (
              <div className="card">
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Poslovi</h2>
                  <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-initial min-w-[160px]">
                      <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                      <input
                        type="text"
                        placeholder="Pretraga (naslov, opis)..."
                        value={jobSearch}
                        onChange={(e) => setJobSearch(e.target.value)}
                        className="input-base pl-9 w-full"
                      />
                    </div>
                    <select
                      value={jobStatusFilter}
                      onChange={(e) => { setJobStatusFilter(e.target.value); setJobPage(1); }}
                      className="input-base w-full sm:w-auto min-w-[140px]"
                    >
                      {JOB_STATUSES.map((s) => (
                        <option key={s.value || 'all'} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {filteredJobs.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">Nema poslova.</p>
                ) : (
                  <>
                    <div className="space-y-4">
                      {filteredJobs.map((job) => (
                        <div key={job.id} className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{job.title}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{job.description}</p>
                              <div className="flex flex-wrap gap-3 mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1"><MdCalendarToday /> {job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString('sr-RS') : '—'}</span>
                                <span className="flex items-center gap-1">
                                  <MdLocationOn />
                                  {typeof job.location === 'string' ? job.location : job.location && typeof job.location === 'object' ? [job.location.street, job.location.city].filter(Boolean).join(', ') || job.location.city || '—' : '—'}
                                </span>
                                <span className="flex items-center gap-1"><MdAttachMoney /> {job.hourlyRate ?? 0} RSD/sat</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                job.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                job.status === 'in_progress' || job.status === 'accepted' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                job.status === 'posted' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {job.status}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleDeleteJob(job.id)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                              >
                                <MdDelete className="text-base" /> Obriši
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {jobsPagination.pages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Strana {jobsPagination.current} / {jobsPagination.pages} (ukupno {jobsPagination.total})
                        </p>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setJobPage((p) => Math.max(1, p - 1))} disabled={jobPage <= 1} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                            <MdChevronLeft className="text-lg" />
                          </button>
                          <button type="button" onClick={() => setJobPage((p) => Math.min(jobsPagination.pages, p + 1))} disabled={jobPage >= jobsPagination.pages} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                            <MdChevronRight className="text-lg" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="card">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Plaćanja</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Pregled plaćanja na platformi — statistika i lista transakcija biće dostupni ovde.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-800/50">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Ukupno plaćanja</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">—</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-800/50">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Na čekanju</p>
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">—</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-800/50">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Ukupan iznos (RSD)</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">—</p>
                  </div>
                </div>
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-gray-500 dark:text-gray-400">
                  Tabela plaćanja i filteri biće dodati u narednoj verziji. Ovde će biti prikazane sve transakcije između korisnika i bebisiterki.
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="card space-y-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Podešavanja admina</h2>

                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
                  </div>
                ) : (
                  <>
                    {/* Verzija aplikacije */}
                    <section className="rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <MdInfo className="text-xl text-primary-600 dark:text-primary-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Verzija aplikacije</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between sm:block">
                          <span className="text-gray-500 dark:text-gray-400">Frontend (web)</span>
                          <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{FRONTEND_VERSION}</span>
                        </div>
                        <div className="flex justify-between sm:block">
                          <span className="text-gray-500 dark:text-gray-400">Backend (API)</span>
                          <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{appInfo?.backendVersion ?? '—'}</span>
                        </div>
                        {appInfo?.nodeEnv && (
                          <div className="flex justify-between sm:block sm:col-span-2">
                            <span className="text-gray-500 dark:text-gray-400">Okruženje</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{appInfo.nodeEnv}</span>
                          </div>
                        )}
                        {appInfo?.timestamp && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 sm:col-span-2">
                            Poslednji odgovor servera: {new Date(appInfo.timestamp).toLocaleString('sr-RS')}
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Notifikacije i podsetnici */}
                    <section className="rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <MdNotifications className="text-xl text-amber-600 dark:text-amber-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notifikacije i podsetnici</h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Jednokratno pokretanje posla podsetnika za plaćanja (7 dana, 3 dana, na dan dospelosti, 3 dana posle). Šalje email i in-app obaveštenja prema podešavanjima korisnika.
                      </p>
                      <button
                        type="button"
                        onClick={handleRunReminders}
                        disabled={runRemindersLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800/50 font-medium text-sm disabled:opacity-50 transition-colors"
                      >
                        {runRemindersLoading ? (
                          <span className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent" />
                        ) : (
                          <MdPlayArrow className="text-lg" />
                        )}
                        Pokreni podsetnike sada
                      </button>
                    </section>

                    {/* Konfiguracija */}
                    <section className="rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <MdBuild className="text-xl text-gray-600 dark:text-gray-400" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Konfiguracija</h3>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 dark:text-gray-400">Režim održavanja</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${adminConfig?.maintenanceMode ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {adminConfig?.maintenanceMode ? 'Uključeno' : 'Isključeno'}
                          </span>
                        </div>
                        {adminConfig?.frontendUrl && (
                          <div className="flex flex-col gap-1">
                            <span className="text-gray-500 dark:text-gray-400">Frontend URL</span>
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{adminConfig.frontendUrl}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                        Režim održavanja i ostale globalne opcije podešavaju se putem promenljivih okruženja na serveru (npr. MAINTENANCE_MODE=true).
                      </p>
                    </section>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Bottom tab bar (mobile only) - isti stil kao regularni dashboard */}
      <nav className="md:hidden fixed bottom-2 left-0 right-0 z-50 px-2 pb-safe pt-2">
        <div className="max-w-lg mx-auto flex items-center justify-around gap-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 px-2">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === '/dashboard/admin'
              ? location.pathname === '/dashboard/admin'
              : location.pathname === item.path && (item.tab ? activeTab === item.tab : true);
            const to = item.tab ? `${item.path}?tab=${item.tab}` : item.path;
            return (
              <Link
                key={item.path + (item.tab || '')}
                to={to}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1.5 px-2 rounded-xl transition-colors ${
                  isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="text-xl flex-shrink-0" />
                <span className="text-[10px] font-medium truncate max-w-full">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen && deleteConfirm.type === 'user'}
        onClose={() => setDeleteConfirm({ isOpen: false, type: null, id: null })}
        onConfirm={confirmDeleteUser}
        title="Kaskadno brisanje korisnika"
        message="Da li ste sigurni? Biće trajno obrisani svi podaci vezani za ovog korisnika: nalog, kalendar, unosi plaćanja, prijateljstva, poruke i konverzacije, notifikacije, poslovi, reference i dostupnost. Ova akcija se ne može poništiti."
        confirmText="Obriši sve"
        cancelText="Otkaži"
        type="danger"
      />
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen && deleteConfirm.type === 'job'}
        onClose={() => setDeleteConfirm({ isOpen: false, type: null, id: null })}
        onConfirm={confirmDeleteJob}
        title="Potvrda brisanja"
        message="Da li ste sigurni da želite da obrišete ovaj posao?"
        confirmText="Obriši"
        cancelText="Otkaži"
        type="danger"
      />
    </div>
  );
}
